import { Card } from '@/components/ui/card';
import { Crown, Trophy, Store, Package, Scissors, Award, Medal, ShoppingBag } from 'lucide-react';

function fmt(n) {
  if (n == null) return '$0';
  return '$' + Number(n).toLocaleString('es-MX', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
}

function PodiumBadge({ rank }) {
  if (rank === 0) return <Crown className="w-4 h-4 text-gold" />;
  if (rank === 1) return <Medal className="w-4 h-4 text-zinc-400" />;
  if (rank === 2) return <Medal className="w-4 h-4 text-orange-600" />;
  return <span className="text-zinc-600 text-xs w-4 text-center">{rank + 1}</span>;
}

export default function RankingSection({ ranking }) {
  const branches = ranking?.branches_ranking || [];
  const productSellers = ranking?.product_sellers_ranking || [];
  const serviceSellers = ranking?.service_sellers_ranking || [];
  const topProducts = ranking?.top_products || [];
  const topServices = ranking?.top_services || [];

  const topBranch = branches[0];
  const topProductSeller = productSellers[0];
  const topServiceSeller = serviceSellers[0];

  return (
    <div className="space-y-4">
      {/* Podium Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <Card className="bg-gradient-to-br from-gold/10 to-zinc-900/50 border-gold/30 p-5" data-testid="podium-branch">
          <div className="flex items-center gap-2 mb-3">
            <Trophy className="w-5 h-5 text-gold" />
            <span className="text-xs text-gold uppercase tracking-wider font-semibold">Sucursal #1</span>
          </div>
          {topBranch && topBranch.revenue > 0 ? (
            <>
              <p className="text-xl font-bold text-white">{topBranch.branch_name}</p>
              <p className="text-2xl font-bold text-gold mt-1">{fmt(topBranch.revenue)}</p>
              <p className="text-xs text-zinc-500 mt-1">{topBranch.transactions} transacciones · Ticket prom. {fmt(topBranch.avg_ticket)}</p>
            </>
          ) : (
            <p className="text-zinc-500 text-sm mt-3">Aún sin ventas este mes</p>
          )}
        </Card>

        <Card className="bg-gradient-to-br from-blue-500/10 to-zinc-900/50 border-blue-500/30 p-5" data-testid="podium-product-seller">
          <div className="flex items-center gap-2 mb-3">
            <Package className="w-5 h-5 text-blue-400" />
            <span className="text-xs text-blue-400 uppercase tracking-wider font-semibold">Top Vendedor Productos</span>
          </div>
          {topProductSeller ? (
            <>
              <p className="text-xl font-bold text-white">{topProductSeller.barber_name}</p>
              <p className="text-2xl font-bold text-blue-400 mt-1">{topProductSeller.units} u.</p>
              <p className="text-xs text-zinc-500 mt-1">{fmt(topProductSeller.revenue)} · {topProductSeller.branch_name}</p>
            </>
          ) : (
            <p className="text-zinc-500 text-sm mt-3">Sin ventas de producto este mes</p>
          )}
        </Card>

        <Card className="bg-gradient-to-br from-green-500/10 to-zinc-900/50 border-green-500/30 p-5" data-testid="podium-service-seller">
          <div className="flex items-center gap-2 mb-3">
            <Scissors className="w-5 h-5 text-green-400" />
            <span className="text-xs text-green-400 uppercase tracking-wider font-semibold">Top Vendedor Servicios</span>
          </div>
          {topServiceSeller ? (
            <>
              <p className="text-xl font-bold text-white">{topServiceSeller.barber_name}</p>
              <p className="text-2xl font-bold text-green-400 mt-1">{fmt(topServiceSeller.revenue)}</p>
              <p className="text-xs text-zinc-500 mt-1">{topServiceSeller.units} servicios · {topServiceSeller.branch_name}</p>
            </>
          ) : (
            <p className="text-zinc-500 text-sm mt-3">Sin ventas de servicio este mes</p>
          )}
        </Card>
      </div>

      {/* Branches Ranking */}
      <Card className="bg-zinc-900/50 border-zinc-800 p-5" data-testid="ranking-branches-card">
        <h3 className="text-sm text-zinc-400 mb-4 flex items-center gap-2">
          <Store className="w-4 h-4" />
          Ranking de Sucursales — Mes Actual
        </h3>
        {branches.length === 0 ? (
          <p className="text-zinc-500 text-sm text-center py-4">No hay sucursales</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-zinc-800">
                  <th className="text-left text-zinc-500 py-2 px-3 font-medium w-8">#</th>
                  <th className="text-left text-zinc-500 py-2 px-3 font-medium">Sucursal</th>
                  <th className="text-right text-zinc-500 py-2 px-3 font-medium">Ventas</th>
                  <th className="text-right text-zinc-500 py-2 px-3 font-medium">Servicios</th>
                  <th className="text-right text-zinc-500 py-2 px-3 font-medium">Productos</th>
                  <th className="text-right text-zinc-500 py-2 px-3 font-medium">Transac.</th>
                  <th className="text-right text-zinc-500 py-2 px-3 font-medium">Ticket Prom.</th>
                </tr>
              </thead>
              <tbody>
                {branches.map((b, idx) => (
                  <tr key={b.branch_id} className="border-b border-zinc-800/50 hover:bg-zinc-800/30" data-testid={`branch-row-${b.branch_id}`}>
                    <td className="py-3 px-3"><PodiumBadge rank={idx} /></td>
                    <td className="py-3 px-3 text-zinc-200">{b.branch_name}</td>
                    <td className="text-right py-3 px-3 text-white font-semibold">{fmt(b.revenue)}</td>
                    <td className="text-right py-3 px-3 text-green-400">{fmt(b.services_revenue)}</td>
                    <td className="text-right py-3 px-3 text-blue-400">{fmt(b.products_revenue)}</td>
                    <td className="text-right py-3 px-3 text-zinc-400">{b.transactions}</td>
                    <td className="text-right py-3 px-3 text-zinc-400">{fmt(b.avg_ticket)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* Product Sellers Ranking */}
      <Card className="bg-zinc-900/50 border-zinc-800 p-5" data-testid="ranking-product-sellers-card">
        <h3 className="text-sm text-zinc-400 mb-4 flex items-center gap-2">
          <Package className="w-4 h-4 text-blue-400" />
          Ranking de Barberos por Venta de Productos
        </h3>
        {productSellers.length === 0 ? (
          <p className="text-zinc-500 text-sm text-center py-4">Aún no hay productos vendidos por barberos este mes</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-zinc-800">
                  <th className="text-left text-zinc-500 py-2 px-3 font-medium w-8">#</th>
                  <th className="text-left text-zinc-500 py-2 px-3 font-medium">Colaborador</th>
                  <th className="text-left text-zinc-500 py-2 px-3 font-medium">Sucursal</th>
                  <th className="text-right text-zinc-500 py-2 px-3 font-medium">Unidades</th>
                  <th className="text-right text-zinc-500 py-2 px-3 font-medium">Ingresos</th>
                </tr>
              </thead>
              <tbody>
                {productSellers.map((s, idx) => (
                  <tr key={s.barber_id} className="border-b border-zinc-800/50 hover:bg-zinc-800/30" data-testid={`product-seller-row-${s.barber_id}`}>
                    <td className="py-3 px-3"><PodiumBadge rank={idx} /></td>
                    <td className="py-3 px-3 text-zinc-200">{s.barber_name}</td>
                    <td className="py-3 px-3 text-xs text-zinc-500">{s.branch_name}</td>
                    <td className="text-right py-3 px-3 text-blue-400 font-semibold">{s.units}</td>
                    <td className="text-right py-3 px-3 text-white">{fmt(s.revenue)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* Service Sellers Ranking */}
      <Card className="bg-zinc-900/50 border-zinc-800 p-5" data-testid="ranking-service-sellers-card">
        <h3 className="text-sm text-zinc-400 mb-4 flex items-center gap-2">
          <Scissors className="w-4 h-4 text-green-400" />
          Ranking de Barberos por Servicios
        </h3>
        {serviceSellers.length === 0 ? (
          <p className="text-zinc-500 text-sm text-center py-4">Aún no hay servicios registrados este mes</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-zinc-800">
                  <th className="text-left text-zinc-500 py-2 px-3 font-medium w-8">#</th>
                  <th className="text-left text-zinc-500 py-2 px-3 font-medium">Colaborador</th>
                  <th className="text-left text-zinc-500 py-2 px-3 font-medium">Sucursal</th>
                  <th className="text-right text-zinc-500 py-2 px-3 font-medium">Servicios</th>
                  <th className="text-right text-zinc-500 py-2 px-3 font-medium">Ingresos</th>
                </tr>
              </thead>
              <tbody>
                {serviceSellers.map((s, idx) => (
                  <tr key={s.barber_id} className="border-b border-zinc-800/50 hover:bg-zinc-800/30" data-testid={`service-seller-row-${s.barber_id}`}>
                    <td className="py-3 px-3"><PodiumBadge rank={idx} /></td>
                    <td className="py-3 px-3 text-zinc-200">{s.barber_name}</td>
                    <td className="py-3 px-3 text-xs text-zinc-500">{s.branch_name}</td>
                    <td className="text-right py-3 px-3 text-green-400 font-semibold">{s.units}</td>
                    <td className="text-right py-3 px-3 text-white">{fmt(s.revenue)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* Top Products + Services */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <Card className="bg-zinc-900/50 border-zinc-800 p-5" data-testid="top-products-card">
          <h3 className="text-sm text-zinc-400 mb-3 flex items-center gap-2">
            <ShoppingBag className="w-4 h-4 text-blue-400" />
            Top Productos Vendidos
          </h3>
          {topProducts.length === 0 ? (
            <p className="text-zinc-600 text-xs text-center py-4">Sin datos</p>
          ) : (
            <ul className="space-y-2">
              {topProducts.map((p, idx) => (
                <li key={`${p.name}-${idx}`} className="flex items-center justify-between bg-zinc-800/30 rounded-sm p-2.5">
                  <div className="flex items-center gap-2">
                    <PodiumBadge rank={idx} />
                    <span className="text-sm text-zinc-200">{p.name}</span>
                  </div>
                  <div className="text-right">
                    <p className="text-sm text-blue-400 font-semibold">{p.units} u.</p>
                    <p className="text-[10px] text-zinc-500">{fmt(p.revenue)}</p>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </Card>

        <Card className="bg-zinc-900/50 border-zinc-800 p-5" data-testid="top-services-card">
          <h3 className="text-sm text-zinc-400 mb-3 flex items-center gap-2">
            <Award className="w-4 h-4 text-green-400" />
            Top Servicios
          </h3>
          {topServices.length === 0 ? (
            <p className="text-zinc-600 text-xs text-center py-4">Sin datos</p>
          ) : (
            <ul className="space-y-2">
              {topServices.map((s, idx) => (
                <li key={`${s.name}-${idx}`} className="flex items-center justify-between bg-zinc-800/30 rounded-sm p-2.5">
                  <div className="flex items-center gap-2">
                    <PodiumBadge rank={idx} />
                    <span className="text-sm text-zinc-200">{s.name}</span>
                  </div>
                  <div className="text-right">
                    <p className="text-sm text-green-400 font-semibold">{fmt(s.revenue)}</p>
                    <p className="text-[10px] text-zinc-500">{s.units} servicios</p>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </Card>
      </div>
    </div>
  );
}
